// Holds the current configurator state as slot names mapped to selected values.
export type ConfigSlots = Record<string, string | undefined>;

// Represents one parsed CSV command, such as "PaintMain:DATA_Jester_Gloss".
export type ConfigCommand = {
  slot: string;
  value: string;
};

// Treats empty cells and "-" from the CSV as "no value".
export function isBlankConfigValue(value?: string) {
  const normalized = value?.trim();
  return !normalized || normalized === "-";
}

// Converts a single "Slot:Value" string into a structured command object.
export function parseConfigCommand(command?: string): ConfigCommand | undefined {
  if (isBlankConfigValue(command)) {
    return undefined;
  }

  const normalizedCommand = command!.trim();
  const separatorIndex = normalizedCommand.indexOf(":");

  if (separatorIndex < 0) {
    return undefined;
  }

  const slot = normalizedCommand.slice(0, separatorIndex).trim();
  const value = normalizedCommand.slice(separatorIndex + 1).trim();

  if (!slot || !value) {
    return undefined;
  }

  return { slot, value };
}

// Converts semicolon-separated cascade commands into structured command objects.
export function parseConfigCommandList(commands?: string): ConfigCommand[] {
  if (isBlankConfigValue(commands)) {
    return [];
  }

  return commands!
    .split(";")
    .map(parseConfigCommand)
    .filter((command): command is ConfigCommand => Boolean(command));
}

// Applies command values to the current config slots and returns the updated state.
export function applyConfigCommands(
  slots: ConfigSlots,
  commands: ConfigCommand[],
): ConfigSlots {
  if (commands.length === 0) {
    return slots;
  }

  return commands.reduce<ConfigSlots>(
    (nextSlots, command) => ({
      ...nextSlots,
      [command.slot]: command.value,
    }),
    { ...slots },
  );
}

// Converts config slots to the Unreal save shape, using keys like "PaintMain_Value".
export function slotsToSavePayload(slots: ConfigSlots) {
  return Object.entries(slots).reduce<Record<string, string | undefined>>(
    (payload, [slot, value]) => {
      if (value) {
        payload[`${slot}_Value`] = value;
      }

      return payload;
    },
    {},
  );
}
